import { NearbyPharmacyFilterModel } from '@/models/pharmacy';
import { DiagnosisType } from '@/types/bff/gql-schema';

export interface PharmacySearchQueries {
  latitude?: string;
  longitude?: string;
  sortType?: `${ENearbyPharmacySearchSortType}`;
  commonFilter?:
    | `${ENearbyPharmacySearchFilterType}`
    | `${ENearbyPharmacySearchFilterType},${ENearbyPharmacySearchFilterType}`
    | `${ENearbyPharmacySearchFilterType},${ENearbyPharmacySearchFilterType},${ENearbyPharmacySearchFilterType}`;
  northEastLatitude?: string;
  northEastLongitude?: string;
  southWestLatitude?: string;
  southWestLongitude?: string;
  viewType?: `${EPharmacyViewType}`;
  diagnosisId?: string;
  diagnosisTypeId?: string;
  treatmentMedicineId?: string;
  treatmentMedicineUnitId?: string;
  treatmentMedicineStrengthId?: string;
}

export enum PharmacySearchParamsQueries {
  SortType = 'sortType',
  CommonFilter = 'commonFilter',
  NorthEastLatitude = 'northEastLatitude',
  NorthEastLongitude = 'northEastLongitude',
  SouthWestLatitude = 'southWestLatitude',
  SouthWestLongitude = 'southWestLongitude',
  Latitude = 'latitude',
  Longitude = 'longitude',
  ViewType = 'viewType',
  DiagnosisTypeId = 'diagnosisTypeId',
  TreatmentMedicineId = 'treatmentMedicineId',
  TreatmentMedicineUnitId = 'treatmentMedicineUnitId',
  TreatmentMedicineStrengthId = 'treatmentMedicineStrengthId',
}

export type PharmacySearchCommonFilterMap = {
  [key in ENearbyPharmacySearchFilterType]: boolean;
};

export interface PharmacyTagVo {
  title: string;
  type: string;
  visualType: EPharmacyTagType;
}

export enum EPharmacyViewType {
  Map = 'map',
  List = 'list',
}

export enum EPharmacyOperationType {
  BeforeOpen = 'BeforeOpen',
  Open = 'Open',
  Closed = 'Closed',
  Lunch = 'Lunch',
  DayOff = 'DayOff',
  BreakTime = 'BreakTime',
  Unknown = 'Unknown',
  ClosedSoon = 'ClosedSoon',
  Absent = 'Absent',
}

export enum EPrescriptionSendType {
  Call = 'CALL',
  SendPrescriptionPartnership = 'SEND_PRESCRIPTION_PARTNERSHIP',
  ClosedPharmacy = 'CLOSED_PHARMACY',
  PrescriptionBookingPartnership = 'PRESCRIPTION_BOOKING_PARTNERSHIP',
  PrescriptionDeniedPharmacy = 'PRESCRIPTION_DENIED_PHARMACY',
  ImmediatePaymentPartnership = 'IMMEDIATE_PAYMENT_PARTNERSHIP',
  SendPrescriptionNowPartnership = 'SEND_PRESCRIPTION_NOW_PARTNERSHIP',
}

export enum EPharmacyTagType {
  PRESCRIPTION_MEDICINE_AVAILABLE = 'PRESCRIPTION_MEDICINE_AVAILABLE',
  PRESCRIPTION_REJECT = 'PRESCRIPTION_REJECT',
  NORMAL = 'NORMAL',
  REQUIRED_CALL = 'REQUIRED_CALL',
  DIET_INJECTION = 'DIET_INJECTION',
  IN_STOCK = 'IN_STOCK',
}

export enum EPharmacyReviewStatus {
  RECEIPT_UPLOAD = 'RECEIPT_UPLOAD', // 영수증 업로드,
  RECEIPT_RECOGNITION_FAILED = 'RECEIPT_RECOGNITION_FAILED', // 영수증 인식 실패
  CONFIRM_SAME_PHARMACY_INFORMATION = 'CONFIRM_SAME_PHARMACY_INFORMATION', // 상호명 일치 약국 정보 확인
  DIFFERENT_PHARMACY_INFORMATION = 'DIFFERENT_PHARMACY_INFORMATION', // 약국 정보가 달라 확인이 필요
  INPUT_PHARMACY_NAME = 'INPUT_PHARMACY_NAME', // 약국 이름 입력
  CONFIRM_DIFFERENT_PHARMACY_INFORMATION = 'CONFIRM_DIFFERENT_PHARMACY_INFORMATION', // 상호명 불일치 약국 정보 확인
  SUBMIT_PHARMACY_REVIEW_INFORMATION = 'SUBMIT_PHARMACY_REVIEW_INFORMATION', // 약국 리뷰 정보 제출
}

export interface PharmacyWriteReviewQueries {
  pharmacyId?: string;
  pharmacyName?: string;
  orderId?: string;
  diagnosisId?: string;
  formEdit?: 'none' | 'edited'; // 수정시 'Y' 아닐시엔 쿼리가 존재하지 않음
  rating?: string;
}

export interface PharmacyWriteReviewForm {
  storeName: string;
  editedPrice?: string;
  editedPaidDate?: string;
  price?: string;
  paidDate?: string | null;
  score: number;
  reviewTagIds: string[];
  reviewDetail?: string;
  storeAddress?: string | null;
  businessNumber?: string | null;
  cardCompany?: string | null;
  cardNumber?: string | null;
}

export enum ENearbyPharmacySearchFilterType {
  CheapPrice = 'CHEAP_PRICE',
  HandlingMedicine = 'HANDLING_MEDICINE',
  IsOpenAtLateNight = 'IS_OPEN_AT_LATE_NIGHT',
  IsOpenEveryDay = 'IS_OPEN_EVERY_DAY',
  Operating = 'OPERATING',
  IncludeDiagnosisPrice = 'INCLUDE_DIAGNOSIS_PRICE',
  ImmediatePayment = 'IMMEDIATE_PAYMENT',
  RequestWithApp = 'REQUEST_WITH_APP',
  DispensationProbability = 'DISPENSATION_PROBABILITY',
  SameMedicine = 'SAME_MEDICINE',
  ConfirmedDispensation = 'CONFIRMED_DISPENSATION',
  HighDispensationProbability = 'HIGH_DISPENSATION_PROBABILITY',
  PossibleDispensation = 'POSSIBLE_DISPENSATION',
  Now = 'NOW',
}

export enum ENearbyPharmacySearchSortType {
  Distance = 'DISTANCE',
  LowPrice = 'LOW_PRICE',
  QuickReceipt = 'QUICK_RECEIPT',
  VisitCount = 'VISIT_COUNT',
  MostReviews = 'MOST_REVIEWS',
  HighRating = 'HIGH_RATING',
}

export enum EPharmacyFilterMode {
  List = 'LIST',
  Toggle = 'TOGGLE',
  Range = 'RANGE',
}

export enum EPharmacyFilterStatus {
  Selected = 'SELECTED',
  Unselected = 'UNSELECTED',
  SubFilterSelected = 'SUB_FILTER_SELECTED',
  SubFilterUnselected = 'SUB_FILTER_UNSELECTED',
}

export interface NearbyPharmacySortsVo {
  activeIconUrl?: string;
  inactiveIconUrl?: string;
  title: string;
  type: ENearbyPharmacySearchSortType;
  isSelected?: boolean;
}

export interface NearbyPharmacySortsAndFiltersVo {
  /** 약국 필터 목록 */
  filters: NearbyPharmacyFilterModel[];
  /** 약국 정렬 목록 */
  sorts: NearbyPharmacySortsVo[];
}

export interface NearbyPharmacyMedicineVo {
  treatmentMedicineId: string;
  treatmentId: string;
  name: string;
  medicineCode?: string;
  description?: string;
  isNew: boolean;
  dispensationUnits: DispensationUnitVo[];
  treatmentMedicineStrengths: TreatmentMedicineStrengthVo[];
}

export interface TreatmentMedicineStrengthVo {
  treatmentMedicineStrengthId: string;
  treatmentMedicineId: string;
  unit: string;
  isDefault: boolean;
}

export interface DispensationUnitVo {
  isDefault: boolean;
  dispensationUnit: number;
  dispensationUnitDisplayName: string;
  treatmentMedicineId: number;
  treatmentMedicineUnitId: number;
  description?: string;
}

export interface NearbyPharmacySearchTabsVo {
  filters: NearbyPharmacyFilterModel[];
  sortTypes: NearbyPharmacySortsVo[];
  id: string;
  name: string;
  diagnosisType?: DiagnosisType | null;
  diagnosisTypeId?: string | null;
  treatmentMedicines: NearbyPharmacyMedicineVo[];
}

export enum EDeliveryCategoryType {
  Pickup = 'PICKUP',
  Post = 'POST',
  Quick = 'QUICK',
  Today_Delivery = 'TODAY_DELIVERY',
  Dawn = 'DAWN',
  Partnership_pickup = 'PARTNERSHIP_PICKUP',
}
export enum EAlternativeType {
  Alternative = 'ALTERNATIVE',
  SameMedicine = 'SAME_MEDICINE',
}
